/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Ganturin Vladimir <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {DOCUMENT} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import {NavigationEnd} from '@angular/router';
import {BreakpointObserver} from '@angular/cdk/layout';

import {NbMenuItem, NbSidebarService} from '@nebular/theme';
import {AuthService, ROLE_ADMIN, ROLE_MANAGER} from 'compob2b-auth';
import {BaseComponent, Contract, Customer, ListFilterService, Settings, StoreService} from 'compob2b-libs';
import {Observable, Subscription} from 'rxjs';
import {filter, map, shareReplay, takeUntil, tap} from 'rxjs/operators';

import {translations} from '../../../../environments/translations.ru';
import {PopupLoginComponent} from '../../../auth/popup-login/login.component';
import {BreadcrumbService} from '../../breadcrumbs/breadcrumbs.service';
import {PopupCompanyComponent} from '../modal-company/modal-company.component';
import {ModalLocationComponent} from '../modal-location/modal-location.component';

import {ModalSuggestIdeaComponent} from '../modal-suggest-idea/modal-suggest-idea.component';
import { NbAccessChecker } from "@nebular/security";
import { DeviceService } from 'projects/compob2b-libs/src/lib/services/device.service';

@Component({
    templateUrl: './layout.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

/**
 * Basic layout component with router-outlet for lazy-loaded modules
 */
export class LayoutComponent extends BaseComponent implements OnInit {
  contract: Contract;
  theme: string;
  isShowBlock = false;
  isShowMenu = true;
  isShowSearch = false;
  isHomePage = true;
  isCatalogPage = false;
  name = 'Angular';
  checked = true;
  settings: Settings;
  menuItems$: Observable<NbMenuItem[]>;
  sideBarState: string;
  iconMenu = true;
  itemsProfile;
  customer: Customer;
  socialLinks: any;
  isShowcase: boolean = false;
  routerData: Subscription;
  showBreadcrumbs: Observable<boolean>;

  isMobile$: Observable<boolean>;
  isMobile = false;

  @ViewChild('sidebar', {read: ElementRef}) sidebarEl: ElementRef;
  private isToggling = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isShowBlock) return;

    const clickedInside = this.sidebarEl?.nativeElement?.contains(event.target as Node);

    if (!clickedInside) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.toggleSidebar();
    }
  }

  readonly customerGroup$ = this.storeService.getCustomerGroup();
  private readonly customer$ = this.storeService.getCustomerAsync();

  constructor(
    private readonly sidebarService: NbSidebarService,
    private readonly storeService: StoreService,
    protected readonly authService: AuthService,
    readonly productFilter: ListFilterService,
    private readonly breadcrumbService: BreadcrumbService,
    public accessChecker: NbAccessChecker,
    private readonly breakpointObserver: BreakpointObserver,
    @Inject(DOCUMENT) private document: Document,
    readonly device: DeviceService,
  ) {
    super();
    this.settings = this.storeService.getSettings();
    this.showBreadcrumbs = this.breadcrumbService.showBreadCrumbs$;
  }

  ngOnInit(): void {
    this.isMobile$ = this.breakpointObserver
      .observe(['(max-width: 1023px)'])
      .pipe(
        map(result => result.matches),
        shareReplay(1)
      );

    if (this.isShowcase || this.authService.isAnonymous()) {
      this.document.body.classList.add('anon-role');
    }

    this.menuItems$ = this.storeService.getMenu().pipe(
      map((response) => {
        return response.map(item => {
          return this.transformMenuItem(item);
        });
      })
    );
    this.setSocialLinks();
    if (!this.isShowcase) {
      this.storeService.initCustomerGroup();
    }
    this.isHome();
    this.isCatalog();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.isHome();
        this.isCatalog();
      });

    this.itemsProfile = this.getItemsProfile();

    this.sideBarState = this.storeService.getSidebarState();
    if (this.sideBarState === 'expanded') {
      this.isShowBlock = true;
      this.isShowMenu = false;
    }

    this.customer$.pipe(
      tap((customer: Customer) => this.handleCustomerResponse(customer)),
      takeUntil(this.unsubscribe)
    ).subscribe();

  }

  private transformMenuItem(item: any): any {
    const customIcons = ['fire-outline', 'custom-icon-1', 'custom-icon-2'];

    if (item.icon) {
      if (customIcons.includes(item.icon)) {
        item.iconPack = 'custom';
      } else {
        item.iconPack = 'eva';
      }
    }
    if (item.children && item.children.length) {
      item.children = item.children.map(child => this.transformMenuItem(child));
    }

    return item;
  }

  setSocialLinks() {
    if (this.settings?.socialMediaSettings) {
      this.socialLinks = {...this.settings.socialMediaSettings};
    }
    else {
      this.socialLinks = {...this.settings};
    }
  }

  private handleCustomerResponse(customer: Customer) {
    this.customer = customer;
    if (customer?.contracts) {
      const index = customer.contracts.findIndex(contract => contract.id === customer.contractId);
      if (index !== -1) {
        this.contract = customer.contracts[index];
      }
    }

    this.cdr.detectChanges();
  }

  toggleSidebar(open = null) {
    if (this.isToggling) return;
    this.isToggling = true;

    if (open !== null) {
      if (open == this.isShowBlock) {
        this.isToggling = false;
        return;
      }
    }

    this.iconMenu = !this.iconMenu;
    this.storeService.toggleSidebarState();
    this.sideBarState = this.storeService.getSidebarState();
    this.isShowBlock = !this.isShowBlock;
    this.isShowMenu = !this.isShowMenu;
    this.sidebarService.toggle(true, 'menu-sidebar');

    setTimeout(() => this.isToggling = false, 0);
  }

  // Переименован из openSearch() — по факту это toggle
  toggleSearch(): void {
    this.isShowSearch = !this.isShowSearch;
    this.cdr.markForCheck();
  }

  toggleFilter(): void {
    const isOpen = !localStorage.getItem('isShowFilter');

    if (isOpen) {
      localStorage.setItem('isShowFilter', '1');
    } else {
      localStorage.removeItem('isShowFilter');
    }

    window.dispatchEvent(new CustomEvent('filterToggle', {
      detail: isOpen
    }));
  }

  get isShowFilter(): boolean {
    return !!localStorage.getItem('isShowFilter');
  }

  open(): void {
    this.windowService.open(PopupLoginComponent, {
      title: 'Вход для пользователя',
      windowClass: 'small',
    });
  }

  openLocationWindow() {
    this.windowService.open(ModalLocationComponent, {windowClass: 'popup'});
  }

  openCompanyWindow() {
    this.windowService.open(PopupCompanyComponent, {windowClass: 'popup'});
  }

  openSuggestIdeaModal($event) {
    $event.preventDefault();
    this.windowService.open(ModalSuggestIdeaComponent, {windowClass: 'popup'});
  }

  toggle(checked: boolean) {
    this.checked = checked;
  }

  private isHome(): void {
    this.isHomePage = this.router.isActive('/cabinet/dashboard', {
      paths: 'exact',
      fragment: 'ignored',
      queryParams: 'ignored',
      matrixParams: 'ignored',
    });
  }

  private isCatalog(): void {
    this.isCatalogPage = this.router.isActive('/catalog', {
      paths: 'exact',
      fragment: 'ignored',
      queryParams: 'ignored',
      matrixParams: 'ignored',
    });
  }

  getItemsProfile() {
    const isManager = this.authService.getRoles().some(role => role === ROLE_ADMIN || role === ROLE_MANAGER);

    if (isManager) {
      return [
        {title: translations.profile, link: '/cabinet/private/account'},
        {title: translations.myCompany, link: '/cabinet/private/company'},
        {title: translations.exit}];
    }
    return [
      {title: translations.profile, link: '/cabinet/private/account'},
      {title: translations.myCompany, link: '/cabinet/private/company'},
      {title: translations.apiTokens, link: '/cabinet/private/account/tokens'},
      {title: translations.notificationCustomer, link: '/cabinet/notifications'},
      {title: translations.offerContract, link: '/cabinet/pages/5'},
      {
        title: translations.exit,
        click: () => {
          this.authService.logout();
        },
      }];
  }

  protected readonly translations = translations
}
